<template>
  <layout :lead="leadInfo" :rooftop="rooftopInfo" :welcome-message="welcomeMessage">
    <section v-if="contents && contents.questions" class="container">
      <vehicle-trade-success v-if="submitted" :sellerPhoneNumber="actionOwner.phone" />
      <base-step-wizard
        v-else
        ref="stepWizard"
        :items="[{ title: 'Vehicle information' }, { title: 'Evaluate' }, { title: 'Upload' }]"
        :initial-step="initialStep"
        :max-step="maxStep"
        use-slots
        show-controls
        @stepChange="handleStepChange"
      >
        <template v-slot:step-0>
          <vehicle-information
            :initial-data="vehicleInformationInitialData"
            :form-options="formOptions"
            :localStorageKey="visitorId"
            @update:form-options="handleUpdateFormOptions"
            @submit="handleFormDataSubmit('form', $event)"
            @change="(newData) => saveLocally(INITIAL_DATA_KEYS.VEHICLE_INFORMATION, newData)"
          />
        </template>
        <template v-slot:step-1>
          <vehicle-evaluation
            :initial-data="vehicleEvaluationInitialData"
            :questions="contents.questions"
            @submit="handleFormDataSubmit('questions', transformVehicleEvaluationData($event))"
            @change="(newData) => saveLocally(INITIAL_DATA_KEYS.VEHICLE_EVALUATION, newData)"
          />
        </template>
        <template v-slot:step-2>
          <vehicle-photos :part-list="contents.photos" @submit="handleFileSubmit" />
        </template>
      </base-step-wizard>
    </section>
  </layout>
</template>

<script>
import { watch, ref } from '@vue/composition-api';
import { mapState } from 'vuex';
import { composeRangeYears } from '@/util';
import Layout from '@/layouts/default';
import { VehicleService } from '@/services';
import VehiclePhotos from '@/components/vehicle-photos';
import BaseStepWizard from '@/components/base-step-wizard';
import VehicleInformation from '@/components/vehicle-information';
import VehicleEvaluation from '@/components/vehicle-evaluation';
import VehicleTradeSuccess from '@/components/vehicle-trade-success';
import useActionMessage from '@/composable/useActionMessage';
import { PAGE_TITLES, ACTION_STATUS } from '@/constants';

const INITIAL_DATA_KEYS = {
  VEHICLE_INFORMATION: 'vehicleInformation',
  VEHICLE_EVALUATION: 'vehicleEvaluation',
  WIZARD_STEP: 'wizardStep',
  MAX_STEP: 'maxStep',
};

export default {
  name: 'TradeWalk',
  metaInfo: {
    title: PAGE_TITLES['trade-walk'],
  },
  components: {
    Layout,
    VehiclePhotos,
    BaseStepWizard,
    VehicleInformation,
    VehicleEvaluation,
    VehicleTradeSuccess,
  },
  setup() {
    const { sentSubmitted, setData, setFiles, actionStatus, actionOwner } = useActionMessage();
    const submitted = ref(false);
    watch(actionStatus, (status) => {
      if (status === ACTION_STATUS.SUBMITTED && !submitted.value) {
        submitted.value = status === ACTION_STATUS.SUBMITTED;
      }
    });
    return {
      sentActionSubmitted: sentSubmitted,
      setActionData: setData,
      setActionFiles: setFiles,
      submitted,
      actionOwner,
    };
  },
  data() {
    return {
      formOptions: {
        trans: ['Automatic', 'Manual'],
        color: [
          'Black',
          'Blue',
          'Brown',
          'Copper',
          'Gold',
          'Gray',
          'Green',
          'Maroon',
          'Orange',
          'Purple',
          'Red',
          'Silver',
          'Tan',
          'White',
          'Yellow',
          'Bronze',
          'Dark Gray',
          'Navy',
        ],
      },
      welcomeMessage: 'Take a few minutes to share with us a little bit about your vehicle.',
      files: [],
      data: {
        form: {},
        questions: [],
      },
      INITIAL_DATA_KEYS,
      vehicleInformationInitialData: {},
      vehicleEvaluationInitialData: {},
      initialStep: 0,
      maxStep: 0,
    };
  },
  // @todo: remove async, promises doesn't delay the creation of the component in vue-2
  async created() {
    const FIELDS_WITH_VARIABLE_OPTIONS = ['color', 'make', 'model', 'year', 'body_style'];

    const restoredData = JSON.parse(window.localStorage.getItem(this.visitorId));
    if (restoredData) {
      this.vehicleInformationInitialData =
        restoredData[INITIAL_DATA_KEYS.VEHICLE_INFORMATION] || {};
      this.vehicleEvaluationInitialData = restoredData[INITIAL_DATA_KEYS.VEHICLE_EVALUATION] || {};
      this.maxStep = restoredData[INITIAL_DATA_KEYS.MAX_STEP] || 0;
      this.initialStep = restoredData[INITIAL_DATA_KEYS.WIZARD_STEP] || 0;

      this.data.form = { ...this.form, ...this.vehicleInformationInitialData };

      if (this.vehicleInformationInitialData) {
        if (this.vehicleInformationInitialData.year) {
          this.vehicleInformationInitialData.year = Number.parseInt(
            this.vehicleInformationInitialData.year
          );

          FIELDS_WITH_VARIABLE_OPTIONS.forEach((field) => {
            if (this.vehicleInformationInitialData[field]) {
              this.formOptions[field] = this.formOptions[field]
                ? [...this.formOptions[field], this.vehicleInformationInitialData[field]]
                : [this.vehicleInformationInitialData[field]];
            }
          });
        }
      }
    }
  },
  computed: {
    ...mapState(['leadInfo', 'rooftopInfo', 'contents']),
    visitorId() {
      return this.$route.query.visitor_id;
    },
  },
  watch: {
    leadInfo(newVal) {
      if (newVal && newVal.companies_id) {
        VehicleService.getVehicleByPublisher().then((result) => {
          const { data: { lists = {} } = {} } = result;
          const nextYear = new Date().getFullYear() + 1;

          Object.keys(lists).forEach((key) => {
            if (!Array.isArray(lists[key])) {
              return;
            }

            // Filter list values from empty strings
            lists[key] = lists[key].filter((item) => {
              if (typeof item === 'string') {
                return item.trim().length > 0;
              }
              return true;
            });

            // adds the values from formOptions to data.lists, removing duplicates by using a Set
            if (this.formOptions[key]) {
              lists[key] = Array.from(new Set([...lists[key], ...this.formOptions[key]]));
            }
          });

          this.formOptions = {
            ...this.formOptions,
            ...{
              ...lists,
              year: composeRangeYears(1984, nextYear),
            },
          };
        });
      }
    },
  },
  methods: {
    validateForm() {
      this.$validator.validateAll().then((result) => {
        if (result) {
          this.active += 1;
        }
      });
    },
    handleFormDataSubmit(step, data) {
      this.data[step] = data;
      this.$refs.stepWizard.next();
    },
    async handleFileSubmit(files) {
      this.$store.commit('setShowAppLoader', true);
      this.files = files;
      await this.submitForm();
    },
    async submitForm() {
      // Restore VehicleEvaluation data from localStorage in case there is data there and the questions have not
      // been refilled
      if (this.data.questions.length === 0 && this.vehicleEvaluationInitialData) {
        this.data.questions = this.transformVehicleEvaluationData(
          this.vehicleEvaluationInitialData
        );
      }

      this.setActionData(this.data);
      this.setActionFiles(this.files);
      await this.sentActionSubmitted();
      this.clearLocalTradeWalkInfo();
      this.$store.commit('setShowAppLoader', false);
    },
    saveLocally(name, value) {
      const leadLocalData = JSON.parse(window.localStorage.getItem(this.visitorId)) || {};
      leadLocalData[name] = value;
      window.localStorage.setItem(this.visitorId, JSON.stringify(leadLocalData));
    },
    clearLocalTradeWalkInfo() {
      window.localStorage.removeItem(this.visitorId);
    },
    handleStepChange(step) {
      this.saveLocally(INITIAL_DATA_KEYS.WIZARD_STEP, step);
      if (step > this.maxStep) {
        this.maxStep = step;
        this.saveLocally(INITIAL_DATA_KEYS.MAX_STEP, this.maxStep);
      }
    },
    handleUpdateFormOptions(formOptions) {
      this.formOptions = {
        ...this.formOptions,
        ...formOptions,
      };
    },
    transformVehicleEvaluationData(data) {
      const transformedData = [];
      for (const key in data) {
        transformedData.push({
          id: key,
          question: this.contents.questions[key],
          answer: data[key],
        });
      }

      return transformedData;
    },
  },
};
</script>
